import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

interface Credentials {
  use_custom_credentials: boolean;
  publer_api_key: string;
  publer_workspace_id: string;
  publer_instagram_account_id: string;
  publer_facebook_account_id: string;
  publer_youtube_account_id: string;
  facebook_access_token: string;
  instagram_access_token: string;
}

interface SocialMediaCredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCredentialsUpdated: () => void;
}

export const SocialMediaCredentialsDialog = ({
  open,
  onOpenChange,
  onCredentialsUpdated,
}: SocialMediaCredentialsDialogProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [credentials, setCredentials] = useState<Credentials>({
    use_custom_credentials: false,
    publer_api_key: "",
    publer_workspace_id: "",
    publer_instagram_account_id: "",
    publer_facebook_account_id: "",
    publer_youtube_account_id: "",
    facebook_access_token: "",
    instagram_access_token: "",
  });

  useEffect(() => {
    if (open) {
      fetchCredentials();
    }
  }, [open]);

  const fetchCredentials = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_social_credentials')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setCredentials({
          use_custom_credentials: data.use_custom_credentials || false,
          publer_api_key: data.publer_api_key || "",
          publer_workspace_id: data.publer_workspace_id || "",
          publer_instagram_account_id: data.publer_instagram_account_id || "",
          publer_facebook_account_id: data.publer_facebook_account_id || "",
          publer_youtube_account_id: data.publer_youtube_account_id || "",
          facebook_access_token: data.facebook_access_token || "",
          instagram_access_token: data.instagram_access_token || "",
        });
      }
    } catch (error) {
      logger.error('Error fetching credentials:', error);
      toast({
        title: "Error",
        description: "Failed to load credentials",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_social_credentials')
        .upsert({
          user_id: user.id,
          ...credentials,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Social media credentials updated successfully",
      });

      onCredentialsUpdated();
      onOpenChange(false);
    } catch (error) {
      logger.error('Error saving credentials:', error);
      toast({
        title: "Error",
        description: "Failed to save credentials",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Social Media Credentials</DialogTitle>
          <DialogDescription>
            Manage your social media API credentials. By default, the Caldwell Group credentials are used.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Use Custom Credentials Toggle */}
            <div className="flex items-center justify-between space-x-2 pb-4 border-b">
              <div className="space-y-0.5">
                <Label htmlFor="use-custom" className="text-base font-semibold">
                  Use Custom Credentials
                </Label>
                <p className="text-sm text-muted-foreground">
                  Override default credentials with your own
                </p>
              </div>
              <Switch
                id="use-custom"
                checked={credentials.use_custom_credentials}
                onCheckedChange={(checked) =>
                  setCredentials({ ...credentials, use_custom_credentials: checked })
                }
              />
            </div>

            {/* Status Badge */}
            <div>
              {credentials.use_custom_credentials ? (
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Using Custom Credentials
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Using Caldwell Group Default
                </Badge>
              )}
            </div>

            {/* Credentials Form - Only show when custom is enabled */}
            {credentials.use_custom_credentials && (
              <>
                {/* Publer Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Publer API</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="publer-api-key">API Key</Label>
                    <Input
                      id="publer-api-key"
                      type="password"
                      placeholder="Enter Publer API key"
                      value={credentials.publer_api_key}
                      onChange={(e) =>
                        setCredentials({ ...credentials, publer_api_key: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="publer-workspace-id">Workspace ID</Label>
                    <Input
                      id="publer-workspace-id"
                      placeholder="Enter Publer workspace ID"
                      value={credentials.publer_workspace_id}
                      onChange={(e) =>
                        setCredentials({ ...credentials, publer_workspace_id: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="publer-instagram-id">Instagram Account ID</Label>
                    <Input
                      id="publer-instagram-id"
                      placeholder="Enter Publer Instagram account ID"
                      value={credentials.publer_instagram_account_id}
                      onChange={(e) =>
                        setCredentials({ ...credentials, publer_instagram_account_id: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="publer-facebook-id">Facebook Account ID</Label>
                    <Input
                      id="publer-facebook-id"
                      placeholder="Enter Publer Facebook account ID"
                      value={credentials.publer_facebook_account_id}
                      onChange={(e) =>
                        setCredentials({ ...credentials, publer_facebook_account_id: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="publer-youtube-id">YouTube Account ID</Label>
                    <Input
                      id="publer-youtube-id"
                      placeholder="Enter Publer YouTube account ID"
                      value={credentials.publer_youtube_account_id}
                      onChange={(e) =>
                        setCredentials({ ...credentials, publer_youtube_account_id: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Graph API Section */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Graph API</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="facebook-token">Facebook Access Token</Label>
                    <Input
                      id="facebook-token"
                      type="password"
                      placeholder="Enter Facebook access token"
                      value={credentials.facebook_access_token}
                      onChange={(e) =>
                        setCredentials({ ...credentials, facebook_access_token: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="instagram-token">Instagram Access Token</Label>
                    <Input
                      id="instagram-token"
                      type="password"
                      placeholder="Enter Instagram access token"
                      value={credentials.instagram_access_token}
                      onChange={(e) =>
                        setCredentials({ ...credentials, instagram_access_token: e.target.value })
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Credentials
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
